<script setup>
// Age-rating badge: renders the official-style PEGI / USK / ESRB label image for
// a rating attribute, falling back to the plain text value when the value (or
// its asset) is unknown. Detection is by the attribute's en_GB label.
const props = defineProps({
  label: { type: String, default: '' },
  value: { type: [String, Number], default: '' },
  imgClass: { type: String, default: 'h-12 w-auto' },
});

const failed = ref(false);

const system = computed(() => {
  const l = props.label.toLowerCase();
  if (l.includes('pegi')) return 'pegi';
  if (l.includes('usk')) return 'usk';
  if (l.includes('esrb')) return 'esrb';
  return null;
});

const src = computed(() => {
  const v = String(props.value ?? '').trim().toLowerCase();
  if (!v) return null;

  if (system.value === 'pegi') {
    // Official PEGI age labels downloaded from pegi.info
    const n = parseInt(v, 10);
    return [3, 7, 12, 16, 18].includes(n) ? `/pegi-icons/pegi-${n}.jpg` : null;
  }
  if (system.value === 'usk') {
    const n = parseInt(v, 10);
    return [0, 6, 12, 16, 18].includes(n) ? `/rating-icons/usk-${n}.svg` : null;
  }
  if (system.value === 'esrb') {
    if (v.startsWith('everyone 10')) return '/rating-icons/esrb-e10.svg';
    if (v.startsWith('everyone')) return '/rating-icons/esrb-e.svg';
    if (v.startsWith('teen')) return '/rating-icons/esrb-t.svg';
    if (v.startsWith('mature')) return '/rating-icons/esrb-m.svg';
    if (v.startsWith('adults')) return '/rating-icons/esrb-ao.svg';
    if (v.startsWith('rating pending') || v === 'rp') return '/rating-icons/esrb-rp.svg';
    return null;
  }
  return null;
});
</script>

<template>
  <!-- Eager on purpose: tiny local assets; lazy-loading never fires for imgs that
       mount inside collapsed panels (ShowMorePanel / hidden tabs). -->
  <img v-if="src && !failed" :src="src" :alt="`${label}: ${value}`" :title="`${label}: ${value}`"
    class="inline-block" :class="imgClass" decoding="async" @error="failed = true" />
  <span v-else class="font-medium text-gray-900">{{ value }}</span>
</template>
